import React, { useState } from 'react';
import { Text, Icon, Button, Spinner } from '../ui/index.jsx';

/**
 * Loading placeholder with animated elements
 */
function PreviewPlaceholder({ phase, generationPhase, filesCount }) {
  const getMessage = () => {
    switch (phase) {
      case 'idle':
        return 'Waiting to start...';
      case 'generating':
        return 'Generating your app...';
      case 'building':
        return 'Building application...';
      default:
        return 'Ready to preview';
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-full w-full bg-[#0D0D10] text-[#06B6D4] p-8">
      {/* Modern Spinner */}
      <div className="relative w-16 h-16 mb-6">
        <div className="absolute inset-0 border-t-2 border-[#06B6D4] rounded-full animate-spin"></div>
        <div className="absolute inset-2 border-r-2 border-[#7B61FF] rounded-full animate-spin-reverse"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <Icon name="code" size="md" className="text-white opacity-50" />
        </div>
      </div>

      <div className="text-center space-y-2">
        <h3 className="text-lg font-medium text-white">{getMessage()}</h3>
        <p className="text-sm text-gray-500 max-w-xs">{phase === 'building' ? 'Compiling assets and starting server...' : 'AI is constructing your layout and logic.'}</p>
      </div>

      {filesCount > 0 && (
        <div className="mt-8 flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/5 text-xs text-gray-400">
          <Icon name="file-code" size="xs" />
          <span>{filesCount} files changed</span>
        </div>
      )}
    </div>
  );
}

/**
 * Device Frame Wrapper
 */
function DeviceFrame({ children, mode, url, onRefresh }) {
  if (mode === 'mobile') {
    return (
      <div className="relative w-full h-full bg-[#121212] rounded-[3rem] border-[8px] border-[#2A2A2A] shadow-2xl overflow-hidden flex flex-col">
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-[#2A2A2A] rounded-b-xl z-20"></div>

        {/* Status Bar */}
        <div className="h-10 bg-white/5 flex items-center justify-between px-6 pt-2 shrink-0 select-none">
          <span className="text-[10px] text-white font-medium">9:41</span>
          <div className="flex gap-1.5">
            <Icon name="wifi" size="xs" className="text-white w-3 h-3" />
            <Icon name="battery" size="xs" className="text-white w-3 h-3" />
          </div>
        </div>

        {/* Browser Bar */}
        <div className="bg-[#1A1A1A] px-4 py-2 flex items-center gap-2 border-b border-white/5 shrink-0">
          <div className="text-gray-400 hover:text-white cursor-pointer"><Icon name="menu" size="xs" /></div>
          <div className="flex-1 bg-[#2A2A2A] h-7 rounded-md flex items-center px-3 text-[10px] text-gray-400 truncate">
            {url ? new URL(url).host : 'localhost:3000'}
          </div>
          <button onClick={onRefresh} className="text-gray-400 hover:text-white"><Icon name="refresh-cw" size="xs" /></button>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 bg-white relative">
          {children}
        </div>

        {/* Home Indicator */}
        <div className="h-5 bg-black w-full flex justify-center items-end pb-1.5 shrink-0 z-20">
          <div className="w-1/3 h-1 bg-white/20 rounded-full"></div>
        </div>
      </div>
    );
  }

  // Default Desktop Browser Frame
  return (
    <div className="flex flex-col w-full h-full rounded-xl bg-[#1E1E24] shadow-2xl overflow-hidden border border-white/10 ring-1 ring-black/50">
      {/* Browser Chrome */}
      <div className="h-10 bg-[#1E1E24] flex items-center px-4 gap-4 select-none shrink-0 border-b border-black">
        {/* Traffic Lights */}
        <div className="flex gap-2">
          <div className="w-3 h-3 rounded-full bg-[#FF5F57] shadow-sm"></div>
          <div className="w-3 h-3 rounded-full bg-[#FEBC2E] shadow-sm"></div>
          <div className="w-3 h-3 rounded-full bg-[#28C840] shadow-sm"></div>
        </div>

        {/* Navigation Controls */}
        <div className="flex gap-2 text-gray-500">
          <Icon name="arrow-left" size="xs" className="opacity-50" />
          <Icon name="arrow-right" size="xs" className="opacity-50" />
          <button onClick={onRefresh} className="hover:text-white transition-colors">
            <Icon name="refresh-cw" size="xs" />
          </button>
        </div>

        {/* Address Bar */}
        <div className="flex-1 flex justify-center">
          <div className="w-full max-w-lg h-7 bg-[#121214] rounded-md flex items-center px-3 text-xs text-gray-400 border border-white/5 mx-auto">
            <Icon name="lock" size="xs" className="mr-2 text-green-500/80" />
            <span className="truncate flex-1 text-center">{url || 'Loading Preview...'}</span>
          </div>
        </div>

        {/* Window Actions */}
        <div className="w-16"></div>
      </div>

      {/* Content Area */}
      <div className="flex-1 bg-white relative">
        {children}
      </div>
    </div>
  );
}

/**
 * DeployPreview component - Live preview iframe
 */
export default function DeployPreview({ previewUrl, phase, generationPhase, filesCount, previewVersion, viewportMode = 'desktop' }) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Build URL with cache buster to force refresh when previewVersion changes
  const iframeSrc = previewUrl ? `${previewUrl}${previewUrl.includes('?') ? '&' : '?'}v=${previewVersion || 0}` : '';

  // Show placeholder if no preview URL
  const showPlaceholder = !previewUrl || phase === 'idle' || phase === 'generating' || phase === 'building';

  // Refresh iframe
  const handleRefresh = () => {
    setIsLoading(true);
    setHasError(false);
    const iframe = document.getElementById('preview-iframe');
    if (iframe) {
      iframe.src = `${previewUrl}${previewUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
    }
  };

  if (showPlaceholder) {
    return (
      <DeviceFrame mode={viewportMode} url={null} onRefresh={() => { }}>
        <PreviewPlaceholder
          phase={phase}
          generationPhase={generationPhase}
          filesCount={filesCount}
        />
      </DeviceFrame>
    );
  }

  return (
    <DeviceFrame mode={viewportMode} url={previewUrl} onRefresh={handleRefresh}>
      <div className="relative w-full h-full bg-[#ffffff]">
        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
            <div className="text-center">
              <Spinner size="lg" className="text-gray-400" />
            </div>
          </div>
        )}

        {/* Error state */}
        {hasError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#F9FAFB] z-10 p-6 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4 text-red-500">
              <Icon name="alert-triangle" size="md" />
            </div>
            <h3 className="text-gray-900 font-medium mb-1">Failed to load preview</h3>
            <p className="text-gray-500 text-sm mb-4">The development server connection was lost.</p>
            <Button variant="secondary" size="sm" onClick={handleRefresh}>
              Try Again
            </Button>
          </div>
        )}

        {/* Preview iframe */}
        <iframe
          id="preview-iframe"
          src={iframeSrc}
          className="w-full h-full border-0 bg-white"
          title="App Preview"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          onLoad={() => {
            setIsLoading(false);
            setHasError(false);
          }}
          onError={() => {
            setIsLoading(false);
            setHasError(true);
          }}
        />
      </div>
    </DeviceFrame>
  );
}
